import chroma from "chroma-js";

// ---------------------------------------------------------------------------
// Chrome color derivation
// ---------------------------------------------------------------------------

/**
 * Chrome colors the Hex client applies to `AppNavbar` (and any other host
 * UI sitting next to the data app) so it blends with the app's theme. The
 * foreground is a near-white or near-black tinted toward the bg's hue;
 * which side of the luminance split is used is picked for readability.
 */
export type DataAppChromeColors = {
  /** Canonical `rgb(...)` / `rgba(...)` form of the sampled background. */
  background: string;
  /** Readable foreground paired with `background` via luminance. */
  foreground: string;
};

type Rgba = { r: number; g: number; b: number; a: number };

// Hex (`#rgb`/`#rrggbb`/`#rrggbbaa`) and `rgb()` / `rgba()` are the formats we
// see in practice (iframe `getComputedStyle()` always emits rgb/rgba; agents
// typically write hex in the manifest). The gate is only there to reject
// formats chroma-js would happily accept but that we don't want to silently
// theme on (named colors, `hsl()`, etc.) — callers treat null as "keep the
// current chrome".
const ACCEPTED_COLOR_PREFIX = /^(#[0-9a-f]+|rgba?\()/i;

function parseCssColor(input: string): Rgba | null {
  const s = input.trim();
  if (!ACCEPTED_COLOR_PREFIX.test(s)) return null;
  try {
    const c = chroma(s);
    const [r, g, b] = c.rgb();
    return { r, g, b, a: c.alpha() };
  } catch (_e) {
    return null;
  }
}

function relativeLuminance(c: Rgba): number {
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

/** WCAG 2 contrast ratio between two relative luminances (1:1 to 21:1). */
function contrastRatio(l1: number, l2: number): number {
  const hi = Math.max(l1, l2);
  const lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

// Hardcoded mirrors of `lightTheme.FontColor.DEFAULT` and `ColorVars.WHITE`.
const HEX_DARK_GRAY1: Rgba = { r: 1, g: 1, b: 27, a: 1 }; // #01011b
const HEX_WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 }; // #ffffff

// Fraction of the anchor blended into the foreground (0 = pure base, 1 = pure anchor).
const DARK_FG_TINT = 0.25;
const LIGHT_FG_TINT = 0.2;

// HSL lightness used to rebuild the bg before mixing — keeps the bg's hue
// while letting the tint factor scale up without dragging fg luminance
// toward bg's. Achromatic bgs skip the rebuild and mix against bg directly.
const LIGHT_FG_ANCHOR_L = 0.85;
const DARK_FG_ANCHOR_L = 0.15;
const ACHROMATIC_SATURATION_THRESHOLD = 0.02;

/**
 * WCAG 2 AA contrast floor for normal text. If the better tinted candidate
 * falls below this against the bg, we drop the hue tint and fall back to a
 * pure white / dark anchor. Visual harmony is preferred, but readability
 * wins when forced to choose.
 */
const MIN_CHROME_CONTRAST = 4.5;

function mixSrgb(base: Rgba, towards: Rgba, t: number): Rgba {
  return {
    r: Math.round(base.r * (1 - t) + towards.r * t),
    g: Math.round(base.g * (1 - t) + towards.g * t),
    b: Math.round(base.b * (1 - t) + towards.b * t),
    a: 1,
  };
}

function rgbString(c: Rgba): string {
  return `rgb(${c.r}, ${c.g}, ${c.b})`;
}

/**
 * Flatten a translucent color over an opaque white backdrop. Iframes default
 * to a white backdrop, so this approximates the *visible* color a user sees
 * inside the data app when its body background uses `rgba()` / `#rrggbbaa`.
 * Without this, the chrome would paint the opaque source color (e.g. fully
 * saturated red for `rgba(255, 0, 0, 0.5)`) instead of the visible pink.
 */
function flattenOverWhite(c: Rgba): Rgba {
  if (c.a >= 1) return c;
  const r = Math.round(c.r * c.a + HEX_WHITE.r * (1 - c.a));
  const g = Math.round(c.g * c.a + HEX_WHITE.g * (1 - c.a));
  const b = Math.round(c.b * c.a + HEX_WHITE.b * (1 - c.a));
  return { r, g, b, a: 1 };
}

/** Rebuild `bg` at the requested HSL lightness; pass through unchanged if achromatic. */
function tintAnchor(bg: Rgba, anchorL: number): Rgba {
  const [h, s] = chroma(bg.r, bg.g, bg.b).hsl();
  if (!Number.isFinite(h) || s < ACHROMATIC_SATURATION_THRESHOLD) return bg;
  const [r, g, b] = chroma.hsl(h, s, anchorL).rgb();
  return { r, g, b, a: 1 };
}

export const CHROME_FG_PERCENT = {
  CONTENT: 90,
  MUTED_CONTENT: 70,
  PLACEHOLDER_CONTENT: 45,
  HOVER: 8,
  ACTIVE: 14,
  DIVIDER: 20,
  CONTROL_BORDER: 25,
  CONTROL_BORDER_HOVER: 40,
} as const;

export function chromeFgAlpha(fg: string, percent: number): string {
  return `color-mix(in srgb, ${fg} ${percent}%, transparent)`;
}

export function flattenChromeFg(
  background: string,
  foreground: string,
  percent: number,
): string {
  return chroma.mix(background, foreground, percent / 100, "rgb").hex();
}

/**
 * Strict per-channel sRGB equality on two CSS color strings; alpha is
 * ignored. Returns false on unparseable inputs.
 *
 * Used by host chrome to decide whether a derived data-app background matches
 * the active Hex theme background — in that case we skip the chrome-theme
 * overlay and let the standard navbar render, since the default Hex chrome
 * already pairs correctly with that bg.
 */
export function isEqualColor(a: string, b: string): boolean {
  const ra = parseCssColor(a);
  const rb = parseCssColor(b);
  if (ra == null || rb == null) return false;
  return ra.r === rb.r && ra.g === rb.g && ra.b === rb.b;
}

/**
 * Build `{ background, foreground }` chrome colors that blend with a data
 * app's theme. Builds a near-white and a near-black candidate, each tinted
 * toward the bg's hue, and picks whichever has higher WCAG 2 contrast
 * against the bg. If even that winner falls below {@link MIN_CHROME_CONTRAST}
 * we drop the hue tint and fall back to the corresponding pure anchor —
 * this matters for mid-tone bgs (sRGB luminance ~0.18–0.5), where a simple
 * `> 0.5` threshold would mis-pick a near-white fg against a too-light bg.
 *
 * Returns null on unparseable or fully transparent input.
 */
export function deriveDataAppChrome(bg: string): DataAppChromeColors | null {
  const rgba = parseCssColor(bg);
  if (rgba == null || rgba.a === 0) return null;

  // Flatten translucent inputs over white (the iframe's default backdrop) so
  // luminance, tint, and the emitted background all reflect the *visible*
  // color rather than the opaque source color.
  const opaque = flattenOverWhite(rgba);
  const lumBg = relativeLuminance(opaque);

  const lightFg = mixSrgb(
    HEX_WHITE,
    tintAnchor(opaque, LIGHT_FG_ANCHOR_L),
    LIGHT_FG_TINT,
  );
  const darkFg = mixSrgb(
    HEX_DARK_GRAY1,
    tintAnchor(opaque, DARK_FG_ANCHOR_L),
    DARK_FG_TINT,
  );

  const lightContrast = contrastRatio(lumBg, relativeLuminance(lightFg));
  const darkContrast = contrastRatio(lumBg, relativeLuminance(darkFg));

  // Prefer the tinted candidate with higher contrast. If it's still below
  // the readability floor, drop the tint and use the pure anchor on the
  // same side — pure white/HEX_DARK_GRAY1 always sits at the extreme end of
  // the luminance scale, so it maximises contrast on its side.
  const background = rgbString(opaque);
  if (darkContrast >= lightContrast) {
    const fg = darkContrast >= MIN_CHROME_CONTRAST ? darkFg : HEX_DARK_GRAY1;
    return { background, foreground: rgbString(fg) };
  }
  const fg = lightContrast >= MIN_CHROME_CONTRAST ? lightFg : HEX_WHITE;
  return { background, foreground: rgbString(fg) };
}

/**
 * The app's chrome colors, or null when the app paints the same background as Hex
 * and there is nothing for the surrounding chrome to match.
 */
export function visibleDataAppChrome(
  chrome: DataAppChromeColors | null,
  hexBackground: string,
): DataAppChromeColors | null {
  return chrome == null || isEqualColor(chrome.background, hexBackground)
    ? null
    : chrome;
}
